import React, { useEffect, useMemo } from 'react';
import PageContainer from '../layouts/PageContainer';
import PageHeader from '../components/Nav/PageHeader';
import CardComponent from '../components/Common/CardComponent';
import StarterPanel from '../components/Dashboard/StarterPanel';
import WifiMetricMiniCard from '../components/Dashboard/WifiMetricMiniCard';
import FiltersBar from '../components/MobileMoney/FiltersBar';
import ActivityLoader from '../components/Common/ActivityLoader';
import { useMobileMoney } from '../DataStore/MobileMoneyContext';
import Pagination from '../components/Common/Pagination';
import { formatMoney, numberFormat } from '../util/araknerdlib';
import { useAuth } from '../DataStore/AuthContext';

const STATUS = {
  0: { label: 'Queued',    badge: 'primary' },
  1: { label: 'Pending',    badge: 'warning' },
  3: { label: 'Successful', badge: 'success' },
  2: { label: 'Failed',     badge: 'danger' }
};


const MobileMoneyMetricsRow = ({ successRate, pending, collectedUGX, currency }) => (
  <div className="d-flex justify-content-end align-items-center py-3 px-4 rounded">
    <WifiMetricMiniCard label="Success Rate" value={`${successRate}%`} iconColor="text-success" icon='fas fa-check-circle'/>
    <div className="border-left mx-3" style={{ height: 30 }} />
    <WifiMetricMiniCard label="Pending" value={numberFormat(pending, 1)} iconColor="text-warning" icon='fas fa-clock'/>
    <div className="border-left mx-3" style={{ height: 30 }} />
    <WifiMetricMiniCard label="Collection (UGX)" value={formatMoney(collectedUGX, currency)} iconColor="text-secondary" icon='fas fa-chart-line'/>
  </div>
);

const MobileMoneyPage = () => {
  const {
    filters, setFilters,
    getMobileKPIs, getMobileMoneyTransactions, setMounted,
    mobileKPIs,
    transactions,
    metaData,
    setCurrentPage,
  } = useMobileMoney();

  const { connectedProfiles } = useAuth();

  // initial fetch
  useEffect(() => {
    getMobileKPIs(filters);
    getMobileMoneyTransactions(filters);
    setMounted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // filtered view (client-side, based on applied filters)
  const filtered = useMemo(() => {
    if (!Array.isArray(transactions)) return [];
    return transactions.filter(d => {
      if (filters.status !== '' && String(d.status) !== String(filters.status)) return false;
      if (filters.from && new Date(d.created_at.replace(' ', 'T')) < new Date(filters.from)) return false;
      if (filters.to && new Date(d.created_at.replace(' ', 'T')) > new Date(filters.to)) return false;
      if (filters.q) {
        const q = filters.q.toLowerCase();
        const hay = `${d.internal_ref ?? ''} ${d.external_ref ?? ''} ${d.phone_number ?? ''} ${d.router ?? ''} ${d.plan_name ?? ''} ${d.narration ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [transactions, filters]);


  // CSV export uses the currently filtered rows
  const exportCSV = () => {
    if (!filtered.length) {
      alert('No data available for export.');
      return;
    }
    const headers = [
      'id','router','wifi_plan','plan_name','mac','ip','amount','phone_number',
      'status','narration','internal_ref','external_ref','created_at','updated_at','signature'
    ];
    const rows = filtered.map(d => ([
      d.id, d.router, d.wifi_plan, d.plan_name || '', d.mac, d.ip, d.amount,
      d.phone_number, STATUS[d.status]?.label, d.narration, d.internal_ref, d.external_ref,
      d.created_at, d.updated_at, d.signature
    ]));
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g,'""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `mobilemoney_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  // When user hits "Apply" in FiltersBar
  const handleApply = () => {
    getMobileMoneyTransactions(true);
    getMobileKPIs(true);
  };

  return (
    <PageContainer>
      <PageHeader
        title="Mobile Money Payments"
        breadcrumb={<li className="breadcrumb-item active" aria-current="page">MobileMoney</li>}
      />

      {/* Header Row: StarterPanel + Right-side KPI mini cards */}
      <div className="row">
        <StarterPanel greetingText='Hi'/>
        <div className="col-lg-8 mb-4">
          <MobileMoneyMetricsRow
            successRate={mobileKPIs.successRate}
            pending={mobileKPIs.pending}
            collectedUGX={mobileKPIs.collected}
            currency={connectedProfiles?.currency || 'UGX'}
          />
        </div>
      </div>

      {/* FiltersBar (Apply-driven) */}
      <FiltersBar
        filters={filters}
        setFilters={setFilters}
        onApply={handleApply}
        onExport={exportCSV}
        onRefresh={() => getMobileMoneyTransactions(true)}
      />

      {/* Table */}
      <CardComponent title="Transactions" subtitle="Live mobile money purchases from the captive portal">
        <div className="table-responsive">
          <table className="table table-hover table-striped align-middle mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Trans Date</th>
                <th>Router</th>
                <th>Plan</th>
                <th>MSISDN</th>
                <th className="text-end">Amount</th>
                <th>Status</th>
                <th>Internal Ref</th>
                {/* <th>MAC</th> */}
                <th>Narration</th>
              </tr>
            </thead>

            {/* Loading state */}
            {transactions === null && (
              <tbody>
                <tr>
                  <td colSpan="10" className="text-center py-4">
                    <ActivityLoader />
                  </td>
                </tr>
              </tbody>
            )}

            {/* Data state */}
              <tbody>
                {Array.isArray(transactions) && transactions.map((d, i) => (
                  <tr key={d.id} title={`IP: ${d.ip} • MAC: ${d.mac}`}>
                    <td>{i + 1}</td>
                    {/* created_at already formatted server-side; avoid timezone surprises */}
                    <td>{d.created_at}</td>
                    <td><span className="badge bg-secondary">{d.router}</span></td>
                    <td>{d.plan_name || d.wifi_plan}</td>
                    <td>{d.phone_number}</td>
                    <td className="text-end">{formatMoney(d.amount, connectedProfiles?.currency)}</td>
                    <td>
                      <span className={`badge badge-${STATUS[d.status*1]?.badge}`}>
                        {STATUS[d.status*1]?.label}
                      </span>
                    </td>
                    <td>{d.internal_ref}</td>
                    {/* <td>{d.mac || <span className="text-muted">—</span>}</td> */}
                    <td className="text-truncate" style={{ maxWidth: 200 }}>{d.narration}</td>
                  </tr>
                ))}

                {transactions === "404" && (
                  <tr>
                    <td colSpan="10" className="text-center text-muted py-4">
                      No mobile money transactions found.
                    </td>
                  </tr>
                )}
              </tbody>
            
          </table>

        </div>

         {metaData && <div className="card-footer d-flex justify-content-between align-items-center">
          <div className="text-muted small">Showing {metaData.total} transaction(s)</div>
          <Pagination
              metaData={metaData}
              onPageChange={(page) => setCurrentPage(page)}
          /> 
        </div>}

        <div className="mt-3 small text-muted">
          <i className="fa fa-info-circle me-1" />
          Payments start as <b>Pending</b>. A remote API callback marks them <b>Successful</b> or <b>Failed</b>. On success, a voucher is generated automatically.
        </div>
      </CardComponent>

      

    </PageContainer>
  );
};

export default MobileMoneyPage;
